import { useCallback, useState } from 'react';

import { todayInZone } from './private-event-dates';
import { EMPTY_FIELDS, type PrivateEventFormFields } from './private-event-form-state';
import type { StepErrors } from './private-event-validation';
import { usePrivateEventLocation } from './use-private-event-location';
import { useStepValidation } from './use-step-validation';
import { useSubmitPrivateEvent } from './use-submit-private-event';
import { usePrivateEventOptions } from './usePrivateEventOptions';

/** Error keys to drop when a field is edited, beyond its own. */
const RELATED_ERRORS: Partial<Record<keyof PrivateEventFormFields, string[]>> = {
  date: ['startsAt', 'endsAt'],
  startTime: ['startTime', 'endsAt'],
  endTime: ['endTime', 'endsAt'],
  dealValue: ['dealValueCents'],
  state: ['state', 'timeZone'],
};

function withoutKeys(errors: StepErrors, keys: string[]): StepErrors {
  const next = { ...errors };
  for (const key of keys) delete next[key];
  return next;
}

/**
 * Create-private-event form state, split out of the drawer so the drawer stays
 * a shell. Region, venue and zone behaviour is `usePrivateEventLocation`;
 * the rules are the pure `private-event-*` modules.
 */
export function usePrivateEventForm(onCreated: (id: string) => void) {
  const [raw, setRaw] = useState<PrivateEventFormFields>(EMPTY_FIELDS);
  const options = usePrivateEventOptions();
  const location = usePrivateEventLocation(raw, setRaw, options);
  const { fields, eventZone } = location;
  const [step, setStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<StepErrors>({});

  const set = useCallback(
    <K extends keyof PrivateEventFormFields>(key: K, value: PrivateEventFormFields[K]) => {
      setRaw((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => withoutKeys(prev, [key, ...(RELATED_ERRORS[key] ?? [])]));
    },
    [],
  );

  const toggleAddOn = useCallback((id: string) => {
    setRaw((prev) => ({
      ...prev,
      addOns: prev.addOns.includes(id) ? prev.addOns.filter((x) => x !== id) : [...prev.addOns, id],
    }));
  }, []);

  const { validateStep, stepReachable, firstInvalidStep } = useStepValidation(
    fields,
    eventZone,
    step,
    setFieldErrors,
  );

  const { submit, submitting, formError } = useSubmitPrivateEvent({
    fields,
    onCreated,
    firstInvalidStep,
    setFieldErrors,
    setStep,
  });

  return {
    ...options,
    fields,
    set,
    toggleAddOn,
    venueOptions: location.venueOptions,
    setRegion: (id: string) => {
      location.setRegion(id);
      setFieldErrors((prev) => withoutKeys(prev, ['regionId', 'venueId', 'timeZone']));
    },
    setLocationMode: (mode: Parameters<typeof location.setLocationMode>[0]) => {
      location.setLocationMode(mode);
      setFieldErrors((prev) => withoutKeys(prev, ['venueId', 'timeZone', 'locationTbd']));
    },
    setTimeZone: (zone: string) => {
      location.setTimeZone(zone);
      setFieldErrors((prev) => withoutKeys(prev, ['timeZone']));
    },
    minDate: todayInZone(eventZone),
    dirty: location.isDirty,
    step,
    setStep,
    submitting,
    formError,
    fieldError: (key: string): string | undefined => fieldErrors[key] || undefined,
    validateStep,
    stepReachable,
    submit,
  };
}

export type PrivateEventForm = ReturnType<typeof usePrivateEventForm>;
